import { ElementProps } from '@/components/sections/builder/types';
import { BlogPostTitleElementDataType } from '@/components/sections/builder/elements/body/blog-post/type';
import { getOverrideAnimationParams } from '@/components/sections/builder/settings/animation-setting';
import PaddingSetting from '@/components/sections/builder/settings/padding-setting';
import { useBuilderStore } from '@/stores/builder';
import clsx from 'clsx';
import { shallow } from 'zustand/shallow';
import { Type } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import InputSelect from '@/components/sections/builder/settings/input-select';
import { Separator } from '@/components/ui/separator';

const defaultData: BlogPostTitleElementDataType = {
  name: 'Title',
  label: 'Our Blog',
  headingSize: 'medium',
  padding: {
    top: 0,
    bottom: 16,
    left: 0,
    right: 0,
  },
};

export const ElementContent = ({ element }: ElementProps) => {
  const data = (element.data || defaultData) as BlogPostTitleElementDataType;

  return (
    <>
      <div
        style={
          {
            '--p-top': `${data.padding?.top || 0}px`,
            '--p-bottom': `${data.padding?.bottom || 0}px`,
            '--p-left': `${data.padding?.left || 0}px`,
            '--p-right': `${data.padding?.right || 0}px`,
          } as React.CSSProperties
        }
      >
        <h2
          data-animation='true'
          {...getOverrideAnimationParams(data.animation)}
          className={clsx(
            'theme_font_heading font-extrabold tracking-tight',
            'p-3',
            'lg:pb-[--p-bottom] lg:pl-[--p-left] lg:pr-[--p-right] lg:pt-[--p-top]',
            data.headingSize === 'large' && 'text-3xl sm:text-4xl md:text-5xl lg:text-6xl',
            data.headingSize === 'medium' && 'text-2xl sm:text-3xl md:text-4xl lg:text-5xl',
            data.headingSize === 'small' && 'text-xl sm:text-2xl md:text-3xl lg:text-4xl'
          )}
        >
          {data.label}
        </h2>
      </div>
    </>
  );
};

export const ElementPreviewIcon = Type;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as BlogPostTitleElementDataType;

  return (
    <div>
      <h1 className='mb-3 text-sm font-bold'>Section Header</h1>
      <div className='mb-3 flex flex-col  gap-2'>
        <h4>Text</h4>
        <Textarea
          name='label'
          value={data.label}
          defaultValue={defaultData.label}
          className='w-full'
          placeholder='Label'
          rows={4}
          maxLength={100}
          onChange={(e) =>
            builder.setChildData(element.parentId as string, element.id, {
              label: e.target.value,
            })
          }
        />
      </div>

      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Heading Size</h4>
        <InputSelect
          defaultValue={data.headingSize || defaultData.headingSize}
          options={[
            { label: 'Small', value: 'small' },
            { label: 'Medium', value: 'medium' },
            { label: 'Large', value: 'large' },
          ]}
          onChange={(v) =>
            builder.setChildData(element.parentId as string, element.id, {
              headingSize: v,
            })
          }
        />
      </div>

      <Separator className='my-4' />

      <h1 className='mb-3 text-sm font-bold'>Style</h1>
      <PaddingSetting
        value={data.padding}
        hideLeft={true}
        hideRight={true}
        onChange={(v) =>
          builder.setChildData(element.parentId as string, element.id, {
            padding: v,
          })
        }
      />
    </div>
  );
}

const BlogPostTitle = {
  type: 'blog_post_title',
  group: 'blog_post',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default BlogPostTitle;
